import React, { useMemo, useState } from "react";
import { Plus, Users } from "lucide-react";
import { useData } from "../context/DataContext";
import { useSearch, matchesQuery } from "../context/SearchContext";
import Select from "../components/Select";
import TenantsTable from "../components/TenantsTable";
import TenantEditor from "../components/TenantEditor";
import HistoryModal from "../components/HistoryModal";
import BulkActionsBar from "../components/BulkActionsBar";
import { ExportTenantsButton, ImportTenantsButton } from "../components/TenantImportExport";
import { computeTenantStatus, currentMonth } from "../utils/format";

const FILTER_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "VACATED", label: "Vacated" },
  { value: "ALL", label: "All" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "paid", label: "Paid" },
  { value: "partial", label: "Partial" },
  { value: "pending", label: "Pending" },
  { value: "overdue", label: "Overdue" },
];

export default function Tenants() {
  const { properties, tenants, payments } = useData();
  const { query } = useSearch();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("ACTIVE");
  const [statusFilter, setStatusFilter] = useState("all");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [history, setHistory] = useState(null); // { scope, tenant }

  const month = currentMonth();
  const rows = useMemo(() => {
    return tenants
      .filter((t) => (filter === "ALL" ? true : t.status === filter))
      .filter((t) => (propertyFilter === "all" ? true : t.propertyId === propertyFilter))
      .filter((t) => {
        if (statusFilter === "all") return true;
        return computeTenantStatus(t, payments, month).toLowerCase() === statusFilter;
      })
      .filter((t) => matchesQuery(query, t.name, t.phone, t.houseNumber));
  }, [tenants, payments, month, filter, statusFilter, propertyFilter, query]);

  const propertyOptions = [
    { value: "all", label: "All properties" },
    ...properties.map((p) => ({ value: p.id, label: p.name })),
  ];

  const openAdd = () => {
    setEditing(null);
    setEditorOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setEditorOpen(true);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Tenants</h2>
            <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
              {rows.length} {rows.length === 1 ? "tenant" : "tenants"}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Add, edit and manage your tenants.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            className="w-40"
            value={propertyFilter}
            onChange={setPropertyFilter}
            options={propertyOptions}
          />
          <Select
            className="w-36"
            value={filter}
            onChange={setFilter}
            options={FILTER_OPTIONS}
          />
          <Select
            className="w-36"
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_OPTIONS}
          />
          <ExportTenantsButton />
          <ImportTenantsButton />
          <button
            className="btn-primary"
            onClick={openAdd}
            disabled={properties.length === 0}
          >
            <Plus className="h-4 w-4" />
            Add tenant
          </button>
        </div>
      </div>

      {properties.length === 0 ? (
        <div className="card p-10 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Add a property first, then you can add tenants.
          </p>
        </div>
      ) : rows.length === 0 ? (
        <EmptyState onAdd={openAdd} />
      ) : (
        <>
          <BulkActionsBar tenants={rows} />
            <TenantsTable
            tenants={rows}
            showProperty
            onEdit={openEdit}
            onOpenHistory={(h) => setHistory(h)}
          />
        </>
      )}

      <TenantEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        editingTenant={editing}
        defaultPropertyId={properties[0]?.id}
      />

      <HistoryModal
        open={!!history}
        onClose={() => setHistory(null)}
        scope={history?.scope}
        tenant={history?.tenant}
      />
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="card p-10 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
        <Users className="h-6 w-6" />
      </div>
      <h3 className="mt-4 text-base font-semibold text-gray-900 dark:text-white">No tenants yet</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Add your first tenant to start tracking rent.</p>
      <button className="btn-primary mt-5" onClick={onAdd}>
        <Plus className="h-4 w-4" />
        Add tenant
      </button>
    </div>
  );
}
